import { Icone, type NomIcone } from './Icone'

/**
 * Les écrans de la console (filtrés par rôle à l'affichage) — TRANSPOSITION de la constante `NAV`.
 *
 * ⚠️ Boîtiers GPS : l'écran avait été retiré de la navigation le 06/10/2026 (le suivi par boîtier ne devait vivre que dans
 * le portail SaaS). DÉCISION INVERSÉE le 14/10/2026 (0052) : l'opératrice a ses PROPRES boîtiers (ceux de la flotte
 * « SwiftDeliver Opérations », exemptée de palier) et a besoin de les nommer — le nom est synchronisé avec Traccar —, de
 * les commander (position, intervalle, redémarrage, moteur) et de régler leurs alertes. « Boîtiers GPS » et « Alertes &
 * zones » reviennent donc, juste après « Livreurs ».
 *
 * Le sous-titre n'est pas décoratif : il dit à l'opératrice ce que l'écran
 * permet de faire, et il s'affiche dans la barre du haut. Le perdre au passage
 * aurait vidé la barre de sa moitié utile.
 */
export const ECRANS = [
  { id: 'dispatch', libelle: 'Dispatch', sous: 'Toutes les courses : les voir, les assigner, les suivre', icone: 'dispatch' },
  /* L'ancienne « Prise d'appel » (décision du 20/10/2026) : les courses arrivent de Danaya, de l'API et de l'app ; la création
     à la main reste pour les exceptions, ouverte depuis le Dispatch, hors de la navigation. */
  { id: 'call', libelle: 'Course manuelle', sous: 'Pour une exception : un client qui appelle ou écrit, créez sa course à la main', icone: 'phone', cache: true },
  { id: 'clients', libelle: 'Clients', sous: 'PME et particuliers qui nous outsourcent leurs livraisons · fiche, forfait, statut', icone: 'users' },
  { id: 'onboarding', libelle: 'Onboarding PME', sous: 'Dossier, offre, contrat signé : une PME ne reçoit des courses qu’une fois active', icone: 'check' },
  { id: 'drivers', libelle: 'Livreurs', sous: 'Notre flotte en propre', icone: 'user' },
  { id: 'boitiers', libelle: 'Boîtiers GPS', sous: 'Nom, rattachement et commandes de nos boîtiers · connexion à Traccar', icone: 'pin' },
  { id: 'historique', libelle: 'Historique GPS', sous: 'Trajets passés, rejeu sur la carte, arrêts et rapports de nos boîtiers', icone: 'clock' },
  { id: 'alertes', libelle: 'Alertes & zones', sous: 'Excès de vitesse, zones, arrêts, boîtiers hors ligne · règles et notifications', icone: 'bell' },
  { id: 'messagerie', libelle: 'Messagerie', sous: 'Clients, courses en cours et livreurs : tous les échanges, en direct', icone: 'chat' },
  { id: 'paie', libelle: 'Paie livreurs', sous: 'Primes de forte demande par livreur et par mois · marquer payé, export', icone: 'wallet' },
  { id: 'compta', libelle: 'Comptabilité', sous: 'Factures & écritures conformes SYSCOHADA · exports comptables', icone: 'receipt' },
  { id: 'reglages', libelle: 'Réglages', sous: 'Grille tarifaire et forfaits de l’offre opérée · frais mobile money', icone: 'gear' },
  { id: 'equipe', libelle: 'Équipe', sous: 'Membres & droits · créés par invitation', icone: 'shield' },
] as const satisfies ReadonlyArray<{ id: string; libelle: string; sous: string; icone: NomIcone; cache?: boolean }>

export type IdEcran = (typeof ECRANS)[number]['id']
export type DefinitionEcran = (typeof ECRANS)[number]

export function Navigation({
  ecrans,
  actif,
  surChangement,
  enAttente,
  nonLus,
  alertes = 0,
}: {
  /** Les écrans ouverts au rôle connecté (voir `roles.ts`). */
  ecrans: readonly DefinitionEcran[]
  actif: IdEcran
  surChangement: (id: IdEcran) => void
  /** Courses en attente d'affectation — pastille sur « Dispatch ». */
  enAttente: number
  /** Messages non lus (livreurs et clients) : pastille sur « Messagerie ». */
  nonLus: number
  /** Alertes GPS à traiter — pastille sur « Alertes & zones ». */
  alertes?: number
}) {
  return (
    <nav className="nav" id="nav">
      {/*
        ⚠️ La pastille lumineuse `.nav-glow` de l'original était positionnée à
        la main en JavaScript (`moveNavGlow`), avec un recalcul au
        redimensionnement et après la transition de largeur de la barre. Ici
        c'est l'élément actif lui-même qui la porte : la position ne peut plus
        se désynchroniser de la sélection, et il n'y a plus rien à recaler.
      */}
      {ecrans.filter((e) => !('cache' in e && e.cache)).map((e) => (
        <button
          key={e.id}
          className={`nav-item${e.id === actif ? ' active' : ''}`}
          data-id={e.id}
          onClick={() => surChangement(e.id)}
          aria-current={e.id === actif ? 'page' : undefined}
        >
          <Icone nom={e.icone} />
          <span className="nl">{e.libelle}</span>
          {e.id === 'dispatch' && enAttente > 0 && <span className="nav-badge">{enAttente}</span>}
          {e.id === 'messagerie' && nonLus > 0 && <span className="nav-badge">{nonLus}</span>}
          {e.id === 'alertes' && alertes > 0 && <span className="nav-badge" aria-label={`${alertes} alerte${alertes > 1 ? 's' : ''} GPS à traiter`}>{alertes > 99 ? '99+' : alertes}</span>}
        </button>
      ))}
    </nav>
  )
}
